import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguageCurrency, formatPhoneNumber } from '../context/LanguageCurrencyContext';

const TermsClient = () => {
  const { language } = useLanguageCurrency();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
      <Navbar />

      {/* Hero Section */}
      <section 
        className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
        style={{ 
          background: 'linear-gradient(135deg, rgba(29, 42, 59, 1) 0%, rgba(29, 42, 59, 0.98) 30%, rgba(213, 181, 119, 0.08) 70%, rgba(29, 42, 59, 0.95) 100%)',
          width: '100%',
          minHeight: 'auto',
        }}
      >
        <div className="w-full max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-1 mx-auto mb-3 rounded-full animate-line-expand" style={{ 
                background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
              }}></div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight relative flex items-center justify-center flex-wrap gap-2 sm:gap-4" style={{ 
              textShadow: '0 0 30px rgba(213, 181, 119, 0.3), 0 4px 8px rgba(0, 0, 0, 0.3)'
            }}>
              <span className="relative inline-flex items-center justify-center gap-2 sm:gap-4">
                {/* Terms Icon - Document/File Icon */}
                <svg 
                  className="w-10 h-10 md:w-12 md:h-12 lg:w-14 lg:h-14 animate-icon-float"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{
                    color: '#D5B577',
                    filter: 'drop-shadow(0 0 10px rgba(213, 181, 119, 0.5))'
                  }}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                {/* Animated Text */}
                <span className="terms-text">
                  {language === 'ar' ? (
                    <span
                      style={{
                        background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                        backgroundSize: '200% 200%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        animation: 'gradient-shift-once 3s ease-in-out forwards',
                      }}
                    >
                      {language === 'ar' ? 'الشروط والأحكام للضيوف' : 'Terms & Conditions for Guests'}
                    </span>
                  ) : (
                    (language === 'ar' ? 'الشروط والأحكام للضيوف' : 'Terms & Conditions for Guests').split('').map((char, index) => (
                      <span
                        key={index}
                        className="inline-block"
                        style={{
                          animationDelay: `${index * 0.08}s`,
                          background: 'linear-gradient(135deg, #D5B577 0%, #f5d89a 50%, #D5B577 100%)',
                          backgroundSize: '200% 200%',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          animation: 'gradient-shift-once 3s ease-in-out forwards',
                        }}
                      >
                        {char === ' ' ? '\u00A0' : char}
                      </span>
                    ))
                  )}
                </span>
                {/* Animated underline */}
                <div className="absolute -bottom-2 left-0 right-0 h-0.5 animate-underline-expand" style={{
                  background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
                  opacity: 0.8
                }}></div>
                {/* Glow effect */}
                <div className="absolute inset-0 animate-pulse-glow" style={{
                  background: 'radial-gradient(circle, rgba(213, 181, 119, 0.2) 0%, transparent 70%)',
                  filter: 'blur(20px)',
                  zIndex: -1
                }}></div>
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-2xl mx-auto leading-relaxed font-light animate-fade-in-up" style={{
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              animationDelay: '0.8s',
              animationFillMode: 'both'
            }}>
              {language === 'ar' ? 'الشروط والأحكام – النسخة المختصرة للمستخدم' : 'Terms & Conditions – Short User Version'}
            </p>
            <div className="w-20 h-1 mx-auto mt-6 rounded-full animate-line-expand-delayed" style={{ 
              background: 'linear-gradient(90deg, transparent, #D5B577, transparent)',
              boxShadow: '0 0 10px rgba(213, 181, 119, 0.5)'
            }}></div>
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={() => setLanguage('ar')}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                  language === 'ar' 
                    ? 'bg-[#D5B577] text-[#1D2A3B]' 
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                العربية
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                  language === 'en' 
                    ? 'bg-[#D5B577] text-[#1D2A3B]' 
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
              >
                English
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
        <div className="w-full max-w-4xl mx-auto">
          <div className="bg-slate-800/50 rounded-2xl p-6 sm:p-8 md:p-10 shadow-xl border border-amber-500/20" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            
            {/* Introduction */}
            <div className="mb-8">
              <p className="text-gray-300 leading-relaxed mb-4" style={{ textAlign: language === 'ar' ? 'right' : 'left', fontSize: '1.1rem' }}>
                {language === 'ar' 
                  ? 'مرحبًا بك في منصة هوستا غلوبال للتنمية العقارية. باستخدامك للموقع أو إتمام أي حجز، فإنك تقر بموافقتك على الشروط التالية:'
                  : 'Welcome to Hosta Global Real Estate Development platform. By using this website or completing a booking, you agree to the following terms:'}
              </p>
            </div>

            {/* Definitions */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'أولاً: التعريفات' : '1. Definitions'}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>الشركة: هوستا غلوبال للتنمية العقارية</li>
                    <li>المالك: مالك العقار المسجل لدى الشركة</li>
                    <li>الضيف: الشخص الذي يقوم بالحجز أو الإقامة في الوحدة السكنية</li>
                  </>
                ) : (
                  <>
                    <li>Company: Hosta Global Real Estate Development</li>
                    <li>Owner: Registered property owner</li>
                    <li>Guest: Any person who makes a booking or stays in the unit</li>
                  </>
                )}
              </ul>
            </div>

            {/* Booking & Stay */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'ثانيًا: الحجز والإقامة' : '2. Booking & Stay'}
              </h2>
              
              <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '1. التوثيق' : '2.1 Identification'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>يجب تقديم أصل الهوية الرسمية لجميع المقيمين عند الوصول.</li>
                    <li>يمنع تسكين أي أشخاص غير مسجلين دون موافقة الشركة.</li>
                  </>
                ) : (
                  <>
                    <li>Valid original ID must be provided for all guests upon arrival.</li>
                    <li>Unregistered guests are not allowed without company approval.</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '2. أثناء الإقامة' : '2.2 During Stay'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>الضيف مسؤول بالكامل عن الوحدة ومحتوياتها.</li>
                    <li>الالتزام بقوانين المنزل وعدم إزعاج الجيران.</li>
                    <li>أي مخالفة قد تؤدي إلى إنهاء الإقامة دون تعويض.</li>
                  </>
                ) : (
                  <>
                    <li>The guest is fully responsible for the unit and its contents.</li>
                    <li>House rules must be respected and neighbors must not be disturbed.</li>
                    <li>Any violation may result in immediate termination without refund.</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '3. مواعيد الدخول والخروج' : '2.3 Check-in / Check-out'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>الدخول: من الساعة 3:00 مساءً</li>
                    <li>الخروج: حتى الساعة 12:00 ظهرًا</li>
                  </>
                ) : (
                  <>
                    <li>Check-in: From 3:00 PM</li>
                    <li>Check-out: Until 12:00 PM</li>
                  </>
                )}
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6" style={{ color: '#D5B577' }}>
                {language === 'ar' ? '4. الخدمات' : '2.4 Services'}
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>يمكن طلب خدمات النظافة عبر حساب المستخدم قبل الموعد بـ 24 ساعة على الأقل.</li>
                    <li>الخدمات الإضافية تخضع لرسوم تحددها الشركة.</li>
                  </>
                ) : (
                  <>
                    <li>Cleaning services can be requested via the user account at least 24 hours in advance.</li>
                    <li>Additional services are subject to extra charges determined by the Company.</li>
                  </>
                )}
              </ul>
            </div>

            {/* Security Deposit */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'ثالثًا: التأمين' : '3. Security Deposit'}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>يتم رد مبلغ التأمين خلال مدة تصل إلى 21 يومًا بعد المغادرة.</li>
                    <li>يتم رد التأمين بالجنيه المصري فقط بعد خصم أي تلفيات مثبتة.</li>
                  </>
                ) : (
                  <>
                    <li>The deposit will be refunded within 21 days after checkout.</li>
                    <li>Refunds are made in Egyptian Pounds only, after deducting any verified damages.</li>
                  </>
                )}
              </ul>
            </div>

            {/* Extension & Vacating */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'رابعًا: التمديد والإخلاء' : '4. Extension & Vacating'}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>التمديد يخضع لتوافر الوحدة والسعر السائد وقت الطلب.</li>
                    <li>في حال عدم الإخلاء في الموعد المحدد، يتحمل الضيف كامل التكاليف وأي خسائر مترتبة.</li>
                  </>
                ) : (
                  <>
                    <li>Extensions depend on availability and prevailing rates.</li>
                    <li>Failure to vacate on time will result in full charges and liability for any related losses.</li>
                  </>
                )}
              </ul>
            </div>

            {/* Liability Limitations */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'خامسًا: حدود المسؤولية' : '5. Liability Limitations'}
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                {language === 'ar' ? 'الشركة غير مسؤولة عن:' : 'The Company is not responsible for:'}
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>الأمتعة الشخصية</li>
                    <li>الظروف الخارجة عن إرادتها</li>
                  </>
                ) : (
                  <>
                    <li>Personal belongings</li>
                    <li>Events beyond its control</li>
                  </>
                )}
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                {language === 'ar' 
                  ? 'تسليم الوحدة عند المغادرة يُعد إقرارًا بإخلاء مسؤولية الشركة عن أي مفقودات.'
                  : 'Handover of the unit confirms release of liability for lost items.'}
              </p>
            </div>

            {/* General Terms */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'سادسًا: أحكام عامة' : '6. General Terms'}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>تخضع هذه الشروط لقوانين جمهورية مصر العربية.</li>
                    <li>استخدام الموقع أو إتمام الحجز يعني الموافقة على هذه الشروط.</li>
                    <li>النسخة القانونية الكاملة هي المرجع المعتمد عند أي نزاع.</li>
                  </>
                ) : (
                  <>
                    <li>These terms are governed by the laws of the Arab Republic of Egypt.</li>
                    <li>Using the platform or completing a booking constitutes acceptance.</li>
                    <li>The full legal version prevails in case of any dispute.</li>
                  </>
                )}
              </ul>
            </div>

            {/* Important Note */}
            <div className="mb-8 bg-amber-500/10 border border-amber-500/30 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'ملاحظة مهمة' : 'Important Note'}
              </h3>
              <ul className="space-y-2 text-gray-300 leading-relaxed" style={{ marginLeft: language === 'ar' ? '0' : '1rem', marginRight: language === 'ar' ? '1rem' : '0' }}>
                {language === 'ar' ? (
                  <>
                    <li>📌 هذه نسخة مختصرة للتوضيح وسهولة الاستخدام.</li>
                    <li>📄 النسخة الكاملة متاحة دائمًا ويمكن الرجوع إليها في أي وقت.</li>
                  </>
                ) : (
                  <>
                    <li>📌 This is a summarized version for clarity and ease of use.</li>
                    <li>📄 The full version is always available and can be referenced at any time.</li>
                  </>
                )}
              </ul>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ color: '#D5B577' }}>
                {language === 'ar' ? 'معلومات الاتصال' : 'Contact Us'}
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                {language === 'ar' ? 'إذا كان لديك أي أسئلة حول هذه الشروط والأحكام، يرجى الاتصال بنا:' : 'If you have any questions about these Terms and Conditions, please contact us:'}
              </p>
              <div className="bg-slate-700/50 rounded-lg p-4 mt-4">
                <p className="text-gray-300 mb-2" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                  <strong style={{ color: '#D5B577' }}>{language === 'ar' ? 'البريد الإلكتروني:' : 'Email:'}</strong> support@hostaglobal.com
                </p>
                <p className="text-gray-300 mb-2" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                  <strong style={{ color: '#D5B577' }}>{language === 'ar' ? 'الهاتف:' : 'Phone:'}</strong> {formatPhoneNumber('+20 100 000 0000', language)}
                </p>
                <p className="text-gray-300" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
                  <strong style={{ color: '#D5B577' }}>{language === 'ar' ? 'العنوان:' : 'Address:'}</strong> {language === 'ar' ? 'القاهرة، مصر' : 'Cairo, Egypt'}
                </p>
              </div>
            </div>

            {/* Acknowledgment */}
            <div className="mt-10 pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-sm text-center" style={{ textAlign: language === 'ar' ? 'right' : 'center' }}>
                {language === 'ar' 
                  ? 'بإنشاء حساب ضيف واستخدام خدمات هوستا جلوبال، فإنك تقر بأنك قد قرأت وفهمت ووافقت على الالتزام بهذه الشروط والأحكام.'
                  : 'By creating a Guest account and using HOSTA GLOBAL\'s services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.'}
              </p>
              <div className="text-center mt-4">
                <Link 
                  to="/register" 
                  className="inline-block px-6 py-2 rounded-lg transition-all duration-200"
                  style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
                >
                  {language === 'ar' ? 'العودة إلى التسجيل' : 'Return to Registration'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      
      {/* CSS Animations */}
      <style>{`
        @keyframes gradient-shift-once {
          0% { 
            background-position: 0% 50%;
            transform: translateY(0);
            opacity: 0;
          }
          50% { 
            background-position: 100% 50%;
            transform: translateY(-3px);
            opacity: 1;
          }
          100% { 
            background-position: 0% 50%;
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes underline-expand {
          0% { 
            width: 0;
            opacity: 0;
          }
          50% {
            opacity: 0.8;
          }
          100% { 
            width: 100%;
            opacity: 0.8;
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            opacity: 0.3;
            transform: scale(1);
          }
          50% { 
            opacity: 0.6;
            transform: scale(1.1);
          }
        }
        
        @keyframes line-expand {
          0% { 
            width: 0;
            opacity: 0;
          }
          100% { 
            width: 80px;
            opacity: 1;
          }
        }
        
        @keyframes line-expand-delayed {
          0% { 
            width: 0;
            opacity: 0;
          }
          50% {
            opacity: 0;
          }
          100% { 
            width: 80px;
            opacity: 1;
          }
        }
        
        @keyframes fade-in-up {
          0% { 
            opacity: 0;
            transform: translateY(20px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes icon-float {
          0%, 100% { 
            transform: translateY(0) rotate(0deg);
          }
          50% { 
            transform: translateY(-8px) rotate(5deg);
          }
        }
        
        .terms-text span {
          animation: letter-float-once 2s ease-out forwards;
        }
        
        @keyframes letter-float-once {
          0% { 
            transform: translateY(20px) rotate(0deg);
            opacity: 0;
          }
          100% { 
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
        }
        
        .animate-line-expand {
          animation: line-expand 1.5s ease-out forwards;
        }
        
        .animate-underline-expand {
          animation: underline-expand 1.2s ease-out 0.5s forwards;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
        
        .animate-line-expand-delayed {
          animation: line-expand-delayed 1.5s ease-out 1s forwards;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
          opacity: 0;
        }
        
        .animate-icon-float {
          animation: icon-float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default TermsClient;
